import { getFromWindowRef } from '@avantia/client-and-server-utilities';
import { HpAnswerMap } from '@avantia/questionset-model';
import { MockPolicyDataMode, multiplePoliciesMode, singlePolicyMode } from '../clientMockData';
import { getConfig } from '../config/configuration';
import { HpClaimsClientConfiguration } from '../config/configurationTypes';
import { registerConfigurationAction } from '../engineClient/config/registerClientConfig';
import { setMetaDataInAnswers } from '../engineClient/tools/clientAndServerUtilities';
import { createCookieStore } from '../engineClient/tools/clientCookies';
import { PerilType } from '../types/policyTypes';

export interface MockPolicyDataModeCookieValue {
  mode: MockPolicyDataMode;
  policyKey?: string;
  answersKey?: keyof PerilType;
}

let allowSiteViewErrorSimulation = false,
  developerModeEnabled = false,
  environment = '';

registerConfigurationAction((config) => {
  ({ allowSiteViewErrorSimulation, developerModeEnabled, environment } = config as HpClaimsClientConfiguration);
});

const cookieStore = createCookieStore();
const agentCookieKey = 'agentId';
const originSiteKey = 'origin';
const simulateSiteViewErrorKey = 'simulateSiteViewError';
const isHolmesEnabledKey = 'isHolmesEnabled';
const mockPolicyDataModeKey = 'mockPolicyDataMode';
const validMockPolicyDataModes: MockPolicyDataMode[] = [singlePolicyMode, multiplePoliciesMode];

export function getAgentCookie(): string | undefined {
  return cookieStore.getItem(agentCookieKey) || undefined;
}

export function setAgentCookie(agentId: string): void {
  cookieStore.setItem({
    name: agentCookieKey,
    value: agentId
  });
}

const azureAdCallbackPath = '/api/azureAdCallback';
const isAgentQueryParam = 'isAgent';

function getWindowLocation(): Location | undefined {
  const windowRef = getFromWindowRef<{ location: Location }>(['location']);
  if (!windowRef) {
    return undefined;
  }

  return windowRef.location;
}

export function isAzureAdCallbackPage(): boolean {
  const location = getWindowLocation();
  if (!location) {
    return false;
  }

  return location.pathname.indexOf(azureAdCallbackPath) >= 0;
}

export function isAzureAdSession(): boolean {
  if (getAgentCookie()) {
    return true;
  }

  const location = getWindowLocation();
  if (location) {
    const params = new URLSearchParams(location.search);
    if (params.get(isAgentQueryParam) === 'true') {
      return true;
    }
  }

  return isAzureAdCallbackPage();
}

export function getOriginSite(): string | null {
  return window.sessionStorage.getItem(originSiteKey);
}

export function setOriginSite(origin: string): void {
  window.sessionStorage.setItem(originSiteKey, origin);
}

export function getShouldSimulateSiteViewErrorClientCookie(): boolean {
  return cookieStore.getItem(simulateSiteViewErrorKey) === 'true' && allowSiteViewErrorSimulation;
}

export function setShouldSimulateSiteViewErrorCookie(siteViewToggle: boolean): void {
  cookieStore.setItem({
    name: simulateSiteViewErrorKey,
    value: String(siteViewToggle)
  });
}

export function getIsHolmesEnabledClientCookie(): boolean {
  return cookieStore.getItem(isHolmesEnabledKey) === 'true';
}

export function setIsHolmesEnabledCookie(isHolmesEnabled: boolean): void {
  cookieStore.setItem({
    name: isHolmesEnabledKey,
    value: String(isHolmesEnabled)
  });
}

export function isLiveEnvironment(): boolean {
  return environment === 'liv';
}

export function getIsMockDataModeEnabled(): boolean {
  return !isLiveEnvironment() && getMockPolicyDataModeCookie() !== null;
}

export function isDeveloperModeEnabled(): boolean {
  return developerModeEnabled;
}

export function getMockPolicyDataModeCookie(): MockPolicyDataModeCookieValue | null {
  const raw = cookieStore.getItem(mockPolicyDataModeKey);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as MockPolicyDataModeCookieValue;
    if ((validMockPolicyDataModes as string[]).includes(parsed.mode)) {
      return parsed;
    }
  } catch {
    // ignore malformed cookie
  }

  return null;
}

export function setMockPolicyDataModeCookie(value: MockPolicyDataModeCookieValue): void {
  cookieStore.setItem({ name: mockPolicyDataModeKey, value: JSON.stringify(value) });
}

export function clearMockPolicyDataModeCookie(): void {
  document.cookie = `${mockPolicyDataModeKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * This will set the agentId and callMediaUser in the answers metaData object or clear it if it's not an agent.
 */
export function setAgentMetaDataInAnswersUsingCookie(answers: HpAnswerMap): void {
  setMetaDataInAnswers({
    productId: getConfig().productIdDefault,
    answers,
    metadata: { agentId: getAgentCookie() },
    validate: true
  });
}
